import { C, U, roleStats } from "./accessors";
import { applyAoeHit } from "./combat";
import { spendMana } from "./mana";
import type { InfiniteWarState, IwTeam } from "./types";
import { killUnit } from "./veil";

export function hitCircle(
  state: InfiniteWarState,
  team: IwTeam,
  x: number,
  y: number,
  radius: number,
  damage: number,
): void {
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.team === team) continue;
    if (Math.hypot(unit.x - x, unit.y - y) > radius + roleStats(unit.roleId).radius) continue;
    applyAoeHit(state, unit, damage, team);
  }
}

export function castShockwave(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost = C.cards.radiant_wave.cost,
): boolean {
    const origin = state.demigods[team];
  const dx = targetX - origin.x;
  const dy = targetY - origin.y;
  const len = Math.hypot(dx, dy);
  if (len < 8) return false;
  if (!spendMana(state, team, cost)) return false;
  const sw = C.shapes.shockwave;
  state.waves.push({
    id: state.nextWaveId++,
    team,
    x: origin.x,
    y: origin.y,
    vx: (dx / len) * sw.speed,
    vy: (dy / len) * sw.speed,
    remain: sw.range,
    hitIds: [],
    damage: sw.damage,
    radius: sw.radius,
  });
  state.events.push({
    kind: "shockwave",
    team,
    x: origin.x,
    y: origin.y,
    angle: Math.atan2(dy, dx),
  });
  return true;
}

export function castCone(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost: number,
): boolean {
    const origin = state.demigods[team];
  const dx = targetX - origin.x;
  const dy = targetY - origin.y;
  const len = Math.hypot(dx, dy);
  if (len < 8) return false;
  if (!spendMana(state, team, cost)) return false;
  const shape = C.shapes.cone;
  const aimAng = Math.atan2(dy, dx);
  const half = (shape.halfAngleDeg * Math.PI) / 180;
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.team === team) continue;
    const ux = unit.x - origin.x;
    const uy = unit.y - origin.y;
    const dist = Math.hypot(ux, uy);
    if (dist > shape.range + roleStats(unit.roleId).radius) continue;
    const ang = Math.atan2(uy, ux);
    let delta = ang - aimAng;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    if (Math.abs(delta) > half) continue;
    applyAoeHit(state, unit, shape.damage, team);
  }
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "cone",
    x: origin.x,
    y: origin.y,
    angle: aimAng,
    radius: shape.range,
  });
  return true;
}

export function castZone(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost: number,
): boolean {
    if (!spendMana(state, team, cost)) return false;
  const shape = C.shapes.zone;
  hitCircle(state, team, targetX, targetY, shape.radius, shape.damage);
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "zone",
    x: targetX,
    y: targetY,
    radius: shape.radius,
  });
  return true;
}

export function castDelayedBlast(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost: number,
): boolean {
    if (!spendMana(state, team, cost)) return false;
  const shape = C.shapes.delayed_blast;
  state.pendingBlasts.push({
    id: state.nextBlastId++,
    team,
    x: targetX,
    y: targetY,
    remain: shape.delaySec,
    radius: shape.radius,
    damage: shape.damage,
  });
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "delayed_blast",
    x: targetX,
    y: targetY,
    radius: shape.radius,
    phase: "telegraph",
  });
  return true;
}

export function castCurseCluster(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost: number,
): boolean {
    if (!spendMana(state, team, cost)) return false;
  const shape = C.shapes.curse_cluster;
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.team === team) continue;
    if (Math.hypot(unit.x - targetX, unit.y - targetY) > shape.radius + roleStats(unit.roleId).radius) {
      continue;
    }
    unit.blight = Math.max(unit.blight, shape.blightSec);
    applyAoeHit(state, unit, shape.damage, team);
    if (unit.phase === "alive" && unit.hp / unit.maxHp <= shape.executeHpRatio) {
      killUnit(state, unit, team);
    }
  }
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "curse_cluster",
    x: targetX,
    y: targetY,
    radius: shape.radius,
  });
  return true;
}

export function castSacrificeNuke(
  state: InfiniteWarState,
  team: IwTeam,
  targetX: number,
  targetY: number,
  cost: number,
): boolean {
    const allies = state.units.filter((u) => u.phase === "alive" && u.team === team);
  if (allies.length === 0) return false;
  let sacrifice = allies[0]!;
  let best = Infinity;
  for (const ally of allies) {
    const d = (ally.x - targetX) ** 2 + (ally.y - targetY) ** 2;
    if (d < best) {
      best = d;
      sacrifice = ally;
    }
  }
  if (!spendMana(state, team, cost)) return false;
  const shape = C.shapes.sacrifice_nuke;
  const cut = Math.max(1, sacrifice.hp * shape.allyHpFrac);
  sacrifice.hp -= cut;
  if (sacrifice.hp <= 0) killUnit(state, sacrifice, team);
  hitCircle(state, team, targetX, targetY, shape.radius, shape.damage);
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "sacrifice_nuke",
    x: targetX,
    y: targetY,
    radius: shape.radius,
  });
  return true;
}

export function castBoardHalf(state: InfiniteWarState, team: IwTeam, cost: number): boolean {
    if (!spendMana(state, team, cost)) return false;
  const field = U.field;
  const midY = field.y + field.h * 0.5;
  const shape = C.shapes.board_half;
  // South presses the northern half; north presses the southern half.
  for (const unit of state.units) {
    if (unit.phase !== "alive" || unit.team === team) continue;
    const inEnemyHalf = team === "south" ? unit.y <= midY : unit.y >= midY;
    if (!inEnemyHalf) continue;
    applyAoeHit(state, unit, shape.damage, team);
  }
  state.events.push({
    kind: "spell_fx",
    team,
    spell: "board_half",
    x: field.x + field.w * 0.5,
    y: midY,
    radius: field.h * 0.5,
  });
  return true;
}

export function tickWaves(state: InfiniteWarState, step: number): void {
  for (const wave of state.waves) {
    const travel = Math.min(wave.remain, C.shapes.shockwave.speed * step);
    wave.x += wave.vx * step;
    wave.y += wave.vy * step;
    wave.remain -= travel;
    for (const unit of state.units) {
      if (unit.phase !== "alive" || unit.team === wave.team || wave.hitIds.includes(unit.id)) {
        continue;
      }
      const r = roleStats(unit.roleId).radius;
      if (Math.hypot(unit.x - wave.x, unit.y - wave.y) <= wave.radius + r) {
        wave.hitIds.push(unit.id);
        applyAoeHit(state, unit, wave.damage, wave.team);
      }
    }
  }
  state.waves = state.waves.filter((w) => w.remain > 0);
}

export function tickPendingBlasts(state: InfiniteWarState, step: number): void {
  for (const blast of state.pendingBlasts) {
    blast.remain -= step;
    if (blast.remain > 0) continue;
    hitCircle(state, blast.team, blast.x, blast.y, blast.radius, blast.damage);
    state.events.push({
      kind: "spell_fx",
      team: blast.team,
      spell: "delayed_blast",
      x: blast.x,
      y: blast.y,
      radius: blast.radius,
      phase: "impact",
    });
  }
  state.pendingBlasts = state.pendingBlasts.filter((b) => b.remain > 0);
}
